/**
 * Filtres du journal rejoués côté client sur les trades analytics.
 *
 * `getAllTradesForAnalytics` n'accepte que compte / dates / stratégies. Les
 * autres filtres de la barre du journal (symbole, sens, statut, tags, bornes de
 * PnL) doivent donc être appliqués au résultat, sans quoi un widget posé à côté
 * de la table parlerait d'un ensemble de trades différent de celle-ci.
 *
 * La sémantique reproduit EXACTEMENT `buildTradeFilters`
 * (`trading-journal/operations.ts`) :
 *   - symbole comparé en majuscules ;
 *   - statut et sens en égalité stricte ;
 *   - tags en « au moins un » (`some`) ;
 *   - bornes de PnL sur le `pnl` BRUT, et un trade sans PnL est exclu dès
 *     qu'une borne est posée (Prisma ne fait jamais matcher un NULL sur gte/lte).
 */

export interface JournalWidgetFilters {
  symbol: string;
  side: "all" | "long" | "short";
  status: "all" | "open" | "closed";
  tagIds: string[];
  minPnl: string;
  maxPnl: string;
}

export function filterTradesForWidgets<T extends Record<string, any>>(
  trades: readonly T[],
  filters: JournalWidgetFilters,
): T[] {
  let list = trades as readonly T[];

  if (filters.symbol) {
    const needle = filters.symbol.toUpperCase();
    list = list.filter((tr) => tr.symbol === needle);
  }
  if (filters.side !== "all") {
    list = list.filter((tr) => tr.type === filters.side);
  }
  if (filters.status !== "all") {
    list = list.filter((tr) => tr.status === filters.status);
  }
  if (filters.tagIds.length > 0) {
    list = list.filter((tr) =>
      (tr.tags ?? []).some((tag: { id: string }) => filters.tagIds.includes(tag.id)),
    );
  }

  const min = filters.minPnl ? parseFloat(filters.minPnl) : undefined;
  const max = filters.maxPnl ? parseFloat(filters.maxPnl) : undefined;
  if (min !== undefined && !Number.isNaN(min)) {
    list = list.filter((tr) => tr.pnl !== null && tr.pnl !== undefined && tr.pnl >= min);
  }
  if (max !== undefined && !Number.isNaN(max)) {
    list = list.filter((tr) => tr.pnl !== null && tr.pnl !== undefined && tr.pnl <= max);
  }

  return [...list];
}
